"use client";

import { gsap } from "gsap";
import Image from "next/image";
import { Send } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import PrimaryButton from "src/components/atoms/PrimaryButton";

gsap.registerPlugin(ScrollTrigger);

const TopComponent = () => {
  const root = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl.from(".hero-eyebrow", { y: 30, opacity: 0, duration: 0.8 })
        .from(".hero-line", { y: 80, opacity: 0, duration: 1.1, stagger: 0.12 }, "-=0.5")
        .from(".hero-sub", { y: 40, opacity: 0, duration: 1 }, "-=0.6")
        .from(".hero-pill", { scale: 0.85, opacity: 0, duration: 0.6, stagger: 0.1 }, "-=0.7")
        .from(".hero-cta", { y: 30, opacity: 0, duration: 0.7 }, "-=0.4")
        .from(".hero-scroll", { opacity: 0, y: 20, duration: 0.8 }, "-=0.3");

      gsap.to(imageRef.current, {
        yPercent: 18,
        ease: "none",
        scrollTrigger: {
          trigger: root.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });
    }, root);

    return () => ctx.revert();
  }, []);
  return (
    <section className="relative hero-section w-full overflow-hidden bg-background-primary !px-4 ">
      {/* Mobile background (light) */}
      <div className="relative block lg:hidden w-full h-[260px] sm:h-[340px] dark:hidden">
        <Image src="/images/serviceBanners/CareerWh.webp" alt="success stories" fill priority className="object-cover object-right" />
      </div>
      {/* Mobile background (dark) */}
      <div className="relative hidden w-full h-[260px] sm:h-[340px] dark:block lg:dark:hidden">
        <Image src="/images/serviceBanners/CareerB.webp" alt="success stories" fill priority className="object-cover object-right" />
      </div>

      {/* Desktop background (wrapper with ref={imageRef} for GSAP) */}
      <div ref={imageRef} className="absolute inset-0 w-full h-full top-0 hidden lg:block">
        <Image src="/images/serviceBanners/CareerWh.webp" alt="success stories" fill priority className="object-contain object-right dark:hidden" />
        <Image src="/images/serviceBanners/CareerB.webp" alt="success stories" fill priority className="object-contain object-right hidden dark:block" />
      </div>

      {/* Light Overlay */}
      <div
        className="absolute inset-0 z-10 hidden lg:block dark:hidden"
        style={{
          background: "linear-gradient(90deg, rgba(240, 242, 255, 0.9) 0%,  transparent 100%)",
        }}
      />
      {/* Dark Overlay */}
      <div
        className="absolute inset-0 z-10 hidden dark:lg:block"
        style={{
          background: "linear-gradient(90deg, #0d0f1e 0%, #0d0f1e 2%,  transparent 100%)",
        }}
      />
      <div className="relative z-20 flex flex-col justify-center px-6! pt-10! ">
        <div className="max-w-2xl ">
          <div className="flex items-center gap-4 mb-4!">
            <h3 className="text-xl sm:text-2xl font-medium uppercase text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600">
              CAREERS AT TYCHO
            </h3>
            <span className="w-16 h-[2px] bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 rounded-full"></span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-4xl xl:text-5xl 2xl:text-6xl font-bold leading-tight mb-6!">
            <span className="hero-title hero-line text-text-primary"> Build Bold.</span>
            <span className="hero-line text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600"> Grow Fast.</span>
          </h1>

          <p className="text-text-secondary text-sm sm:text-base lg:text-[16px] 2xl:text-lg  md:max-w-[90vw] lg:max-w-[40vw] leading-relaxed !mb-12 w-full">
            Join a dynamic workplace where innovation, technology, and creativity come together to build meaningful digital solutions. At Tycho Technologies, we empower passionate professionals to accelerate their careers, develop strong technical expertise, and thrive in a culture driven by collaboration, continuous learning, and innovation. Work on real world projects across web development, mobile applications, UI UX design, and emerging technologies while learning from experienced industry experts. We provide an environment that supports skill development, problem solving, and leadership opportunities. Grow with us and contribute to building scalable, future ready digital products that create real impact and deliver long term value globally.
          </p>

          <PrimaryButton size="lg" icon={<Send />} href="#recent-openings" className="mb-0! md:mb-16! !px-6 !py-2 ">
            Send Application
          </PrimaryButton>
        </div>
      </div>
      <style jsx>{`
        .hero-section {
          min-height: 80vh;
        }

        @media screen and (min-width: 1024px) and (max-width: 1440px) and (max-height: 850px) {
          .hero-section {
            min-height: 550px;
          }
        }

        @media screen and (min-width: 1024px) and (max-height: 768px) {
          .hero-section {
            min-height: 500px;
          }
        }

        @media screen and (min-width: 1441px) {
          .hero-section {
            min-height: 650px;
          }
        }
      `}</style>
    </section>
  );
};

export default TopComponent;
